import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import format from 'date-fns/format';
import { IGustoWidgetDataResponse } from '../../../hooks/query/useGetGustoDataForWidget';
import { formatValueWithoutCurrency } from '../../../helpers/currency.helper';
import { getNumOfDaysLeft } from '../../../helpers/datesAndTime.helper';
import { WidgetLink } from '../components/WidgetLink';
import { ReactComponent as ReloadIcon } from '../../../assets/svg/reload.svg';
import { ReactComponent as RedErrorIcon } from '../../../assets/svg/shared/icon-red-white-error.svg';
import { ReactComponent as GustoWidgetIcon } from '../../../assets/svg/vendors/gusto-icon.svg';
import { VIEWS_MAP } from '../../../router/constants';
import { GustoWidgetSkeleton } from '../components/skeletons/GustoWidgetSkeleton';
import { reportActOnSpeedBump, reportViewSpeedBump } from '../../../analytics/speedBump.analytics';
import { ActionModal } from '../../../DesignSystem/ActionModal';

interface GustoWidgetProps {
  gustoData: IGustoWidgetDataResponse;
  balance: number;
  isLoading: boolean;
}

interface IGustoLoadingErrorWidgetProps {
  title: string;
  text: string;
}

interface IGustoTokenErrorWidgetProps extends IGustoLoadingErrorWidgetProps {
  onClick: () => void;
}

const ExpiredToken = ({ title, text, onClick }: IGustoTokenErrorWidgetProps) => {
  const { t } = useTranslation('dashboardFaceliftResourcesEN');
  return (
    <div className='flex flex-col items-center h-full bg-bw00 pt-[64px]'>
      <div className='text-center text-bw08'>
        <h1 className='font-metricRSemibold text-sm mb-[5px]'>{title}</h1>
        <p className='font-metricR text-sm'>{text}</p>
      </div>
      <button
        type='button'
        onClick={onClick || (() => {})}
        className='mt-[36px] px-[14px] py-[6px] bg-action03 text-bw00 text-[12px] font-metricRSemibold w-[170px] h-[32px] rounded-[2px] hover:bg-action04 active:bg-action05'
      >
        {t('GUSTO.WIDGET.RECONNECT_BUTTON')}
      </button>
    </div>
  );
};

const LoadingError = ({ title, text }: IGustoLoadingErrorWidgetProps) => (
  <div className='flex flex-col items-center h-full bg-bw00 pt-[47px]'>
    <ReloadIcon />
    <div className='text-center text-bw08'>
      <h1 className='font-metricRMedium text-sm mb-[9px]'>{title}</h1>
      <p className='font-metricR text-sm'>{text}</p>
    </div>
  </div>
);

const getGustoWidgetErrorKeys = (paymentAmount: number, accountBalance: number) => {
  if (paymentAmount > accountBalance) return 'INSUFFICIENT_BALANCE';
  return '';
};
export const GustoWidgetV2 = ({ gustoData, balance, isLoading }: GustoWidgetProps) => {
  const { t } = useTranslation('dashboardFaceliftResourcesEN');
  const { t: bigT } = useTranslation();

  const { paymentAmount, nextPaymentDate, gustoLink } = gustoData || {
    paymentAmount: 0,
    nextPaymentDate: '',
    gustoLink: null,
  };
  const softErrorResKey = getGustoWidgetErrorKeys(paymentAmount, balance);
  const canRenderUpcomingPayment = paymentAmount && nextPaymentDate && gustoLink;
  const [showLeaveWarning, setShowLeaveWarning] = useState<boolean>(false);

  const handleShowSpeedBump = () => {
    setShowLeaveWarning(true);
    reportViewSpeedBump('Gusto widget');
  };

  const handleRedirectOutside = () => {
    reportActOnSpeedBump('Continue');
    if (gustoLink) {
      window.open(gustoLink, '_blank');
    }
    setShowLeaveWarning(false);
  };

  const handleCancelRedirectOutside = () => {
    reportActOnSpeedBump('Back');
    setShowLeaveWarning(false);
  };

  if (isLoading) return <GustoWidgetSkeleton />;
  const renderPaymentAmountRow = () => (
    <div className='flex justify-between mb-[35px]'>
      <div className='font-metricR text-bw06 text-md leading-6'>{t('GUSTO_WIDGET_PAYMENT_TITLE')}</div>
      <div className='text-right'>
        <div className='font-metricR text-bw07 text-md leading-6' id='gustoWidgetPaymentAmount'>
          $
          <span className='font-metricRBold'>{formatValueWithoutCurrency(paymentAmount, 2)}</span>
        </div>
        {softErrorResKey && (
          <div className='font-metricR text-red02 text-sm flex items-center'>
            <RedErrorIcon className='w-[10px] h-[10px] mr-[3px] relative top-[-1px]' />
            {t(softErrorResKey)}
          </div>
        )}
      </div>
    </div>
  );

  const renderPaymentDateRow = () => {
    const formattedPaymentDate = format(new Date(nextPaymentDate), 'MMMM dd, yyyy');
    const calculateRemainingDaysLeft = getNumOfDaysLeft(nextPaymentDate);
    const calcStr =
      calculateRemainingDaysLeft === 0
        ? 'Today'
        : `${calculateRemainingDaysLeft} ${calculateRemainingDaysLeft > 1 ? 'days' : 'day'}`;
    return (
      <div className='flex justify-between'>
        <div className='font-metricR text-bw06 text-md leading-6'>{t('GUSTO_WIDGET_DATE_TITLE')}</div>
        <div className='text-right'>
          <div className='font-metricRMedium text-bw07 text-md leading-6' id='gustoWidgetNextPaymentDate'>
            {formattedPaymentDate}
          </div>
          <div className='font-metricR text-bw06 text-sm' id='gustoWidgetDaysLeft'>
            {calculateRemainingDaysLeft === 0 ? '' : 'In'}
            <span className='ml-[3px] font-metricRSemibold'>{calcStr}</span>
          </div>
        </div>
      </div>
    );
  };
  const renderUpcomingPayment = () => (
    <div className='border-b-[1px] border-bw03 px-[23px] py-[32px]'>
      {renderPaymentAmountRow()}
      {renderPaymentDateRow()}
    </div>
  );
  const renderHeader = () => (
    <div className='h-[99px] flex items-start justify-between mx-[16px] pt-[24px]'>
      <h1 className='font-metricRSemibold text-bw08 self-start text-sm'>{t('GUSTO_WIDGET_TITLE')}</h1>
      <GustoWidgetIcon className='rounded-[6px] w-[60px] h-[60px]' />
    </div>
  );
  const renderFooter = () => (
    <div className='h-[63px] flex items-center justify-end pr-[18px]'>
      <WidgetLink
        linkTitle={t('GUSTO_WIDGET_LINK_TITLE')}
        linkUrl=''
        testId='GustoWidget__link'
        onClick={handleShowSpeedBump}
      />
    </div>
  );
  const handleGustoExpiredToken = () => {
    window.location.href = `/${VIEWS_MAP.DASHBOARD}?flow_id=gusto`;
  };
  const renderErrorState = () => {
    const { gustoError } = gustoData || {};
    // Code 50692 is for expired token
    if (gustoError?.errorCode === 50692) {
      return <ExpiredToken title={gustoError.title} text={gustoError.text} onClick={handleGustoExpiredToken} />;
    }

    // Code 50693 is for loading error
    if (gustoError?.errorCode === 50693) {
      return <LoadingError title={gustoError.title} text={gustoError.text} />;
    }
    return null;
  };
  return (
    <div className='bg-bw00 flex flex-col w-full rounded-[12px]'>
      {renderHeader()}
      {canRenderUpcomingPayment && renderUpcomingPayment()}
      {canRenderUpcomingPayment && renderFooter()}
      {!canRenderUpcomingPayment && renderErrorState()}
      {showLeaveWarning && (
        <ActionModal
          title={bigT('NAVIGATE_OUTSIDE_MODAL.TITLE')}
          description={bigT('NAVIGATE_OUTSIDE_MODAL.DESCRIPTION')}
          onSubmit={handleRedirectOutside}
          onCancel={handleCancelRedirectOutside}
          cancelButtonTitle={bigT('NAVIGATE_OUTSIDE_MODAL.CANCEL')}
          mainButtonTitle={bigT('NAVIGATE_OUTSIDE_MODAL.CONTINUE')}
          disclaimerText={bigT('NAVIGATE_OUTSIDE_MODAL.DISCLAIMER')}
        />
      )}
    </div>
  );
};
